import React, { memo, useEffect, useRef, useState } from "react";
import { Bookmark, Lightbulb, Maximize2 } from "lucide-react";
import { AppButton, FormField, Input, MathText, cn } from "../ui";
import ImageLightbox from "./ImageLightbox";

/**
 * QuestionPane — one question: its header, its stem and media, its answer
 * control and, when the author wrote one, its hint.
 *
 * Options are real <input type="radio|checkbox"> inside <label>, not buttons.
 * A screen reader must announce "option 2 of 4, selected" — a <button> cannot
 * say that. The input is visually hidden and the letter beside it carries the
 * state, so the design gives up none of the semantics.
 *
 * Renders WITHOUT card chrome: the runner puts it and the action row in one card.
 */

const CHOICE_TYPES = ["single_choice", "multi_select", "true_false", "image_mcq", "code_conceptual"];
const TEXT_TYPES = ["text_answer", "fill_blank"];
const DIFFICULTIES = { easy: "Easy", medium: "Medium", hard: "Hard" };

const optionLabel = (idx) => String.fromCodePoint(65 + idx); // A, B, C…
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Hints are authored as lines or "•" bullets. */
const splitHint = (hint) =>
  String(hint || "")
    .split(/\r?\n|(?:^|\s)[•·]\s*/)
    .map((line) => line.trim())
    .filter(Boolean);

function QuestionPane({
  question,
  answer,
  index,
  total,
  marks,
  penalty,
  onAnswer,
  onFlag,
  fontScale = 1,
  selectable = true,
}) {
  const stemRef = useRef(null);
  const [zoomed, setZoomed] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  // Move focus to the stem on every question change. Without this a keyboard
  // user lands wherever the previous question left them.
  useEffect(() => {
    stemRef.current?.focus?.({ preventScroll: true });
    // A lightbox or hint left open across a navigation would belong to the
    // previous question.
    setZoomed(false);
    setHintOpen(false);
  }, [question?._id]);

  if (!question) return null;

  const isMulti = question.questionType === "multi_select";
  const selected = (answer?.selectedOptionIds || []).map(String);
  const groupName = `q-${question._id}`;
  const difficulty = DIFFICULTIES[question.difficulty] || null;
  const flagged = !!answer?.isFlagged;
  const hintLines = splitHint(question.hint);

  const toggleOption = (optionId) => {
    const id = String(optionId);
    if (isMulti) {
      onAnswer({
        selectedOptionIds: selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id],
      });
    } else {
      onAnswer({ selectedOptionIds: [id] });
    }
  };

  return (
    <section aria-label={`Question ${index + 1} of ${total}`} className="tw-flex tw-flex-col tw-gap-6">
      <header className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-x-4 tw-gap-y-2">
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
          <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
            Question {index + 1}
            <span className="tw-font-normal tw-text-content-muted"> of {total}</span>
          </p>
          <p className="tw-m-0 tw-flex tw-flex-wrap tw-gap-x-2 tw-text-xs tw-text-content-muted">
            <span>+{plural(marks, "mark")}</span>
            {penalty > 0 && <span>· −{penalty} if wrong</span>}
            {difficulty && <span>· {difficulty}</span>}
            {isMulti && <span>· More than one answer</span>}
          </p>
        </div>

        <AppButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onFlag(!flagged)}
          aria-pressed={flagged}
          className={cn(flagged && "tw-text-warning-fg")}
        >
          <Bookmark size={15} aria-hidden="true" fill={flagged ? "currentColor" : "none"} />
          {flagged ? "Marked for review" : "Mark for review"}
        </AppButton>
      </header>

      <div
        ref={stemRef}
        tabIndex={-1}
        aria-live="polite"
        className={cn(
          "tw-whitespace-pre-line tw-break-words tw-font-medium tw-leading-relaxed tw-text-content tw-outline-none",
          !selectable && "tw-select-none",
        )}
        style={{ fontSize: `${1.0625 * fontScale}rem` }}
      >
        <MathText as="div" text={question.questionText} />
      </div>

      {question.imageUrl && (
        <figure className="tw-m-0 tw-flex tw-flex-col tw-items-start tw-gap-2">
          <img
            src={question.imageUrl}
            alt=""
            className="tw-max-h-80 tw-max-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface tw-object-contain"
          />
          <AppButton type="button" variant="ghost" size="sm" onClick={() => setZoomed(true)}>
            <Maximize2 size={14} aria-hidden="true" /> View full size
          </AppButton>
        </figure>
      )}

      {question.codeSnippet && (
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <span className="tw-text-xs tw-font-medium tw-text-content-muted">
            {question.codeLanguage ? `${titleCase(question.codeLanguage)} snippet` : "Code snippet"}
          </span>
          {/* Focusable so a keyboard user can scroll a long line sideways. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <pre
            tabIndex={0}
            className="tw-m-0 tw-overflow-x-auto tw-rounded-md tw-border tw-border-solid tw-border-code-border tw-bg-code tw-p-4 tw-font-mono tw-text-[0.8125rem] tw-leading-relaxed tw-text-code-fg tw-focus-ring"
          >
            <code>{question.codeSnippet}</code>
          </pre>
        </div>
      )}

      {CHOICE_TYPES.includes(question.questionType) && (
        <div
          role={isMulti ? "group" : "radiogroup"}
          aria-label={isMulti ? "Answer options, select all that apply" : "Answer options"}
          className={cn("tw-flex tw-flex-col tw-gap-2.5", !selectable && "tw-select-none")}
        >
          {(question.options || []).map((opt, i) => {
            const checked = selected.includes(String(opt._id));
            return (
              <label
                key={opt._id}
                className={cn(
                  "tw-m-0 tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-px-4 tw-py-3",
                  "tw-transition-colors tw-duration-150",
                  "has-[:focus-visible]:tw-outline has-[:focus-visible]:tw-outline-2 has-[:focus-visible]:tw-outline-offset-2 has-[:focus-visible]:tw-outline-primary",
                  checked
                    ? "tw-border-primary tw-bg-primary/[0.06]"
                    : "tw-border-input-line tw-bg-surface-raised hover:tw-border-primary/40 hover:tw-bg-surface-hover",
                )}
              >
                <input
                  className="tw-sr-only"
                  type={isMulti ? "checkbox" : "radio"}
                  name={groupName}
                  checked={checked}
                  onChange={() => toggleOption(opt._id)}
                />
                <span
                  aria-hidden="true"
                  className={cn(
                    "tw-mt-px tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-border tw-border-solid",
                    "tw-text-xs tw-font-semibold tw-transition-colors",
                    isMulti ? "tw-rounded-[6px]" : "tw-rounded-full",
                    checked
                      ? "tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg"
                      : "tw-border-input-line tw-bg-surface tw-text-content-muted",
                  )}
                >
                  {optionLabel(i)}
                </span>
                <span
                  className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2 tw-whitespace-pre-line tw-break-words tw-pt-0.5 tw-leading-relaxed tw-text-content"
                  style={{ fontSize: `${0.9375 * fontScale}rem` }}
                >
                  <MathText text={opt.text} />
                  {opt.imageUrl && (
                    <img
                      src={opt.imageUrl}
                      alt=""
                      className="tw-max-h-48 tw-max-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-object-contain"
                    />
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {TEXT_TYPES.includes(question.questionType) && (
        <FormField label="Your answer" className="tw-max-w-xl">
          <Input
            size="lg"
            autoComplete="off"
            placeholder="Type your answer"
            value={answer?.textAnswer || ""}
            onChange={(e) => onAnswer({ textAnswer: e.target.value })}
          />
        </FormField>
      )}

      {question.questionType === "numeric_answer" && (
        <FormField
          label="Your answer"
          hint="Numbers only. Use a decimal point if needed."
          className="tw-max-w-xs"
        >
          <Input
            size="lg"
            inputMode="decimal"
            autoComplete="off"
            placeholder="Enter a number"
            value={answer?.textAnswer || ""}
            // Kept as text with a filter rather than type="number": a number
            // input silently discards what it cannot parse mid-typing, so "1e"
            // vanishes and the candidate watches their answer disappear.
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^-?\d*\.?\d*$/.test(v)) onAnswer({ textAnswer: v });
            }}
          />
        </FormField>
      )}

      {/* A hint is a help the candidate CHOOSES to take — printing it beside the
          question would hand it to people who never asked and change what the
          paper measures. */}
      {hintLines.length > 0 && (
        hintOpen ? (
          <div className="tw-flex tw-gap-3 tw-rounded-md tw-bg-muted/60 tw-px-4 tw-py-3 tw-text-sm tw-leading-relaxed tw-text-content">
            <Lightbulb size={16} aria-hidden="true" className="tw-mt-0.5 tw-shrink-0 tw-text-content-muted" />
            <div className="tw-flex tw-flex-col tw-gap-1">
              {hintLines.map((line, i) => (
                <p key={i} className="tw-m-0"><MathText text={line} /></p>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <AppButton type="button" variant="ghost" size="sm" onClick={() => setHintOpen(true)}>
              <Lightbulb size={15} aria-hidden="true" /> Show hint
            </AppButton>
          </div>
        )
      )}

      {zoomed && question.imageUrl && (
        <ImageLightbox
          src={question.imageUrl}
          alt={`Figure for question ${index + 1}`}
          onClose={() => setZoomed(false)}
        />
      )}
    </section>
  );
}

export default memo(QuestionPane);
